import { Button, Card, Form, Input } from "antd";
import { AddressElement, CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { useContext, useEffect, useRef, useState } from "react";
import SignupFormContext from "../../../../contexts/forms/SignupFormContext";
import StripeContext from "../../../../contexts/stripe/StripeContext";
import PlansContext from "../../../../contexts/plans/PlansContext";
import { Authentication } from "@leedpulse-monorepo/classes";
import { createPortal } from "react-dom";
import { IPlan } from '@leedpulse-monorepo/interfaces';
import './StripePaymentForm.css';

interface StripePaymentFormProps {
    planId: string;
    companyId: string;
    companyApiKey: string;
    auth: Authentication;
    professionalNumber: string;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({ planId, companyId, companyApiKey, auth, professionalNumber }) => {
    const { data, actions } = useContext(SignupFormContext);
    const { formData, submitButtonTarget, canProceed, isLoading } = data;
    const { updateFormData, handleSubmitAccountCreation, setSubmitError } = actions;
    const { clientSecret, createSubscription } = useContext(StripeContext);
    // const { planCache, fetchPlanById } = useContext(PlansContext);

    const stripe = useStripe();
    const elements = useElements();

    const [isProcessing, setIsProcessing] = useState(false);
    // const [plan, setPlan] = useState<IPlan | null>(null);

    // Address collected by Stripe AddressElement
    const [addressComplete, setAddressComplete] = useState(false);
    const addressDataRef = useRef<StripeAddressElementChangeEvent['value'] | null>(null);

    // // Fetch plan details for price display
    // useEffect(() => {
    //     if (!planId) return;
    //     const cached = planCache.get(planId);
    //     if (cached) {
    //         setPlan(cached);
    //         return;
    //     }
    //     fetchPlanById(planId).then((p) => {
    //         if (p) setPlan(p);
    //     });
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [planId]);

    /**
     * Called whenever the Stripe AddressElement value changes.
     * We store the latest complete value in a ref to avoid unnecessary re-renders.
     */
    const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
        setAddressComplete(event.complete);
        if (event.complete) {
            addressDataRef.current = event.value;
        }
    };

    /**
     * Two-step subscription flow:
     * 1. confirmCardSetup() - securely confirms the card via SetupIntent
     * 2. createSubscription() - creates the Stripe subscription with the confirmed payment method
     *    and saves the billing/shipping address on the Stripe customer
     */
    const handleSubscribe = async () => {
        try {
            setIsProcessing(true);
            setSubmitError(null);

            if (!stripe || !clientSecret) throw new Error('Stripe not initialized');

            const cardNumberElement = elements?.getElement(CardNumberElement);
            if (!cardNumberElement) throw new Error('Card elements not initialized');

            if (!addressDataRef.current) {
                setSubmitError('Veuillez remplir l\'adresse de facturation');
                return;
            }

            const addressValue = addressDataRef.current;

            // Step 1 - Confirm the card via SetupIntent with billing details from AddressElement
            const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
                clientSecret,
                {
                    payment_method: {
                        card: cardNumberElement,
                        billing_details: {
                            name: formData.paymentData.cardOwner || addressValue.name || `${formData.firstName} ${formData.lastName}`,
                            email: formData.email,
                            phone: addressValue.phone || formData.phone,
                            address: addressValue.address,
                        },
                    },
                }
            );

            if (setupError) {
                setSubmitError(setupError.message || 'Une erreur est survenue lors de la vérification de la carte');
                return;
            }

            if (!setupIntent?.payment_method) {
                setSubmitError('Impossible de récupérer le moyen de paiement confirmé');
                return;
            }

            // Step 2 - Create the subscription with the confirmed payment method
            const paymentMethodId = typeof setupIntent.payment_method === 'string'
                ? setupIntent.payment_method
                : setupIntent.payment_method.id;

            await createSubscription({
                companyId,
                companyApiKey,
                auth,
                planId,
                professionalNumber,
                paymentMethodId,
                address: addressValue.address,
                billingName: addressValue.name,
            });

            // Step 3 - Mark account creation as complete
            handleSubmitAccountCreation();
        } catch (error) {
            console.error("Error during subscription:", error);
            setSubmitError(
                error instanceof Error
                    ? error.message
                    : 'Une erreur est survenue lors de la souscription'
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const loading = isLoading || isProcessing;

    const updatePaiementData = (value: {
        cardOwner?: string;
    }) => {
        updateFormData('paymentData', {
            ...formData.paymentData,
            ...value,
        });
    };

    return (
        <>
            {/* ——— Card details ——— */}
            <Card title="Informations de paiement">
                <div className="stripe-payment-form-card" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                    <Form.Item label="Titulaire de la carte" style={{ gridColumn: 'span 2' }}>
                        <Input placeholder="Jean Dupont" value={formData.paymentData.cardOwner} onChange={(e) => updatePaiementData({ cardOwner: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Numéro de carte" style={{ gridColumn: 'span 2' }}>
                        <CardNumberElement className="stripe-card-element" />
                    </Form.Item>
                    <Form.Item label="Date d'expiration">
                        <CardExpiryElement className="stripe-card-element" />
                    </Form.Item>
                    <Form.Item label="Code de sécurité">
                        <CardCvcElement className="stripe-card-element" />
                    </Form.Item>
                </div>
            </Card>

            {/* ——— Billing address ——— */}
            <Card title="Informations de facturation">
                <div className="stripe-address-element" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                    <AddressElement
                        options={{
                            mode: 'billing',
                            defaultValues: {
                                name: formData.paymentData.cardOwner || `${formData.firstName} ${formData.lastName}`,
                                phone: formData.phone,
                            },
                            fields: {
                                phone: 'always',
                            },
                            validation: {
                                phone: { required: 'never' },
                            },
                        }}
                        onChange={handleAddressChange}
                    />
                </div>
            </Card>

            {submitButtonTarget?.current && createPortal(
                <Button
                    type="primary"
                    onClick={handleSubscribe}
                    loading={loading}
                    disabled={!canProceed || !addressComplete || loading}
                >
                    Souscrire et créer mon compte
                </Button>
                , submitButtonTarget.current)}
        </>
    );
};

export default StripePaymentForm;
